import { usePromisedRequest as promiseRequest } from "../Common/request";
import { resolveNoContent, resolveWithBody } from "./Utils/utils";

export type EventSettingsTypes = {
    enablePaymentConfirmationUploads: boolean;
    enablePaymentsAfterDueDate: boolean;
    automaticallyMakeNewRegistrationCandidate: boolean;
    earlyVerificationBlocked: boolean;
    earlyVerificationPeriod: number;
    queueVerificationBlocked: boolean;
    queueVerificationLimit: number;
    enableOnlinePayments: boolean;
    "results-public-access": boolean;
    autoDqWhenCategoryBeginsEnabled: boolean;
    useBirthDateForCategoryMatching: boolean;
    sharePublicFightsQueue: boolean;
};

export type SettingType = "bool" | "int" | "string";

export type SingleEventSetting<K extends keyof EventSettingsTypes = keyof EventSettingsTypes> = {
    name: K;
    type: SettingType;
    value: EventSettingsTypes[K];
};

export type ResetSettingResponse<K extends keyof EventSettingsTypes = keyof EventSettingsTypes> = {
    defaultValue: EventSettingsTypes[K];
};

const eventSettingsApi = {
    listSettings<K extends keyof EventSettingsTypes>(eventId: number, keys: K[] = []): Promise<SingleEventSetting<K>[]> {
        const url =
            keys.length === 0 ? `/api/admin/events/${eventId}/settings` : `/api/admin/events/${eventId}/settings?keys=` + keys.join(",");

        return promiseRequest<SingleEventSetting<K>[]>({
            url: url,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    resetSetting<K extends keyof EventSettingsTypes>(eventId: number, key: K): Promise<ResetSettingResponse<K>> {
        return promiseRequest<ResetSettingResponse<K>>({
            url: `/api/admin/events/${eventId}/settings/${key}`,
            method: "delete",
            json: true,
        }).then(resolveWithBody());
    },
    updateSetting<K extends keyof EventSettingsTypes>(eventId: number, key: K, value: EventSettingsTypes[K]): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/settings/${key}`,
            method: "put",
            json: {
                value: value,
            },
        }).then(resolveNoContent());
    },
    getSingleSetting<K extends keyof EventSettingsTypes>(eventId: number, key: K): Promise<SingleEventSetting<K>> {
        return promiseRequest<SingleEventSetting<K>>({
            url: `/api/admin/events/${eventId}/settings/${key}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
};

export default eventSettingsApi;
